import { captureException } from '@/utils/sentry';
import { ReadedEmlJson, readEml } from 'eml-parse-js';
import { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@/contexts/theme-context';
import type {
  ImageMapArea,
  InboundEmlAttachment,
} from './attachment-list-types';

type EmlParsingStatus = 'idle' | 'loading' | 'success' | 'error';

const parseEml = (emlContent: string): Promise<ReadedEmlJson> => {
  return new Promise((resolve, reject) => {
    readEml(emlContent, (err, readEmlJson) => {
      if (err) {
        reject(err);
        return;
      }

      if (!readEmlJson) {
        reject(new Error('No EML JSON found'));
        return;
      }

      resolve(readEmlJson);
    });
  });
};

export const useParsedEmlAttachment = (emlAttachment: InboundEmlAttachment) => {
  const [status, setStatus] = useState<EmlParsingStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [parsedEml, setParsedEml] = useState<{
    html?: string;
    text?: string;
  }>({});

  const fetchEml = useCallback(async (emlAttachmentUrl: string) => {
    setStatus('loading');

    try {
      const response = await fetch(emlAttachmentUrl);
      if (!response.ok) {
        setStatus('error');
        setError(new Error(`HTTP error fetching EML: ${response.status}`));
        return;
      }

      // RN seemingly has trouble handling responses where the response contains encoded binary data (which EML files
      // can contain). Because of this, we can't use functions like `response.text()` as it'll just return undefined
      // becuase RN doesn't know how to handle it. So we have to first get the data as an array buffer and then decode
      // the data as a UTF-8 string using `TextDecoder`.
      const arrayBuffer = await response.arrayBuffer();
      const decoder = new TextDecoder('utf-8');
      const emlContent = decoder.decode(arrayBuffer);

      if (emlContent.length > 0) {
        const parsedEml = await parseEml(emlContent);

        setParsedEml({
          html: parsedEml.html,
          text: parsedEml.text,
        });

        setStatus('success');
        setError(null);
      } else {
        const error = new Error('EML content empty');
        captureException(error, {
          extra: {
            emlAttachmentUrl,
          },
        });

        setStatus('error');
        setError(error);
      }
    } catch (e) {
      captureException(e, {
        extra: {
          emlAttachmentUrl,
        },
      });

      setStatus('error');
      setError(e as Error);
    }
  }, []);

  useEffect(() => {
    if (!emlAttachment.url || emlAttachment.url.length === 0) {
      return;
    }

    fetchEml(emlAttachment.url);
  }, [emlAttachment.url, fetchEml]);

  return { error, parsedEml, status };
};

export const ATTACHMENT_HTML_SEARCH_INPUT_HIGHLIGHT_CLASS =
  'search-input-highlight';

export const ATTACHMENT_HTML_SELECTED_SEARCH_INPUT_CLASS =
  'selected-search-input';

export const ATTACHMENT_HTML_SEARCH_INPUT_UUID_DATA_ATTRIBUTE =
  'data-search-input-uuid';

export const useHTMLHighlighElementCSSStyles = () => {
  const theme = useTheme();

  return `
/* !important so row highlights win over table styling injected into the
   attachment HTML (e.g. CSV-to-HTML zebra striping: tr:nth-child(even)). */
.${ATTACHMENT_HTML_SEARCH_INPUT_HIGHLIGHT_CLASS} {
  background-color: ${theme.sk.color.bg.highlight.secondary} !important;
  cursor: pointer;
}
.${ATTACHMENT_HTML_SEARCH_INPUT_HIGHLIGHT_CLASS}:hover {
  background-color: ${theme.sk.color.bg.highlight.primary} !important;
}
.${ATTACHMENT_HTML_SELECTED_SEARCH_INPUT_CLASS} {
  background-color: ${theme.sk.color.bg.highlight.primary} !important;
}
  `;
};

interface GenerateImageHighlightInjectionScriptInput {
  errorEvent: string;
  highlightColor: string;
  imageAreaClickEvent: string;
  imageHighlights: Record<string, ImageMapArea[]>;
  selectedAreaID?: string;
  selectedColor: string;
  updateSelectionEvent: string;
}

export const generateImageHighlightInjectionScript = ({
  highlightColor,
  imageAreaClickEvent,
  imageHighlights,
  selectedAreaID,
  selectedColor,
  updateSelectionEvent,
  errorEvent,
}: GenerateImageHighlightInjectionScriptInput): string => {
  return `
const imageHighlights = ${JSON.stringify(imageHighlights)};
const selectedAreaID = ${JSON.stringify(selectedAreaID ?? '')};
const HIGHLIGHT_COLOR = '${highlightColor}';
const SELECTED_COLOR = '${selectedColor}';

function createSVGOverlay(img, areas) {
    const imgRect = img.getBoundingClientRect();
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;
    
    if (!naturalWidth || !naturalHeight) {
      return null;
    }
    
    const aspectRatio = imgRect.width / naturalWidth;
    
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', imgRect.width.toString());
    svg.setAttribute('height', imgRect.height.toString());
    svg.setAttribute('viewBox', \`0 0 \${naturalWidth * aspectRatio} \${naturalHeight * aspectRatio}\`);
    svg.style.position = 'absolute';
    svg.style.top = '0';
    svg.style.left = '0';
    svg.style.pointerEvents = 'none';
    
    // we create a polygon for each area
    areas.forEach((area) => {
      const polygon = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      
      // we need to scale the points according to the aspect ratio
      const points = area.coordinates
        .map(([x, y]) => \`\${x * aspectRatio},\${y * aspectRatio}\`)
        .join(' ');
      
      polygon.setAttribute('points', points);
      polygon.setAttribute('fill', area.id === selectedAreaID ? SELECTED_COLOR : HIGHLIGHT_COLOR);
      polygon.setAttribute('opacity', area.id === selectedAreaID ? '0.6' : '0.4');
      polygon.setAttribute('stroke', 'transparent');
      polygon.setAttribute('data-area-id', area.id);
      polygon.style.cursor = 'pointer';
      polygon.style.pointerEvents = 'all';
      
      polygon.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const clickedAreaId = e.currentTarget.getAttribute('data-area-id');
        
        const message = {
          type: '${imageAreaClickEvent}',
          data: {
            areaId: clickedAreaId,
            imageUrl: img.src
          }
        };
        
        if (typeof window.ReactNativeWebView !== 'undefined') {
          window.ReactNativeWebView.postMessage(JSON.stringify(message));
        } else if (window.parent !== window) {
          window.parent.postMessage(message, '*');
        }
      });
      
      svg.appendChild(polygon);
    });
    
    return svg;
  }
  
  function wrapImageWithHighlights(img, areas) {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.display = 'inline-block';
    wrapper.style.lineHeight = '0';
    wrapper.style.pointerEvents = 'none';
    
    img.parentNode.insertBefore(wrapper, img);
    
    wrapper.appendChild(img);
    
    img.style.pointerEvents = 'auto';
    
    const svg = createSVGOverlay(img, areas);
    if (svg) {
      wrapper.appendChild(svg);
    }
  }
  
function setupImageHighlights() {
  Object.keys(imageHighlights).forEach((imageUrl) => {
    const areas = imageHighlights[imageUrl];
    if (!areas || areas.length === 0) return;

    const images = document.querySelectorAll(\`img[src="\${imageUrl}"]\`);

    images.forEach((img) => {
      if (img.parentElement?.hasAttribute('data-highlight-wrapper')) return;

      const processImage = () => {
        if (img.complete && img.naturalWidth > 0) {
          wrapImageWithHighlights(img, areas);
          img.parentElement?.setAttribute('data-highlight-wrapper', 'true');
        }
      };

      if (img.complete && img.naturalWidth > 0) {
        processImage();
      } else {
        img.addEventListener('load', processImage, { once: true });
      }
    });
  });
}

window.updateImageHighlightSelection = function(newSelectedAreaID) {
  document.querySelectorAll('svg polygon[data-area-id]').forEach((polygon) => {
    const areaId = polygon.getAttribute('data-area-id');
    const isSelected = areaId === newSelectedAreaID;
    polygon.setAttribute('fill', isSelected ? SELECTED_COLOR : HIGHLIGHT_COLOR);
    polygon.setAttribute('opacity', isSelected ? '0.6' : '0.4');
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupImageHighlights);
} else {
  setupImageHighlights();
}

// Listen for messages from React Native
document.addEventListener('message', function(event) {
  try {
    const message = JSON.parse(event.data);
    if (message.type === '${updateSelectionEvent}') {
      const selectedAreaID = message.data.selectedAreaID;
      window.updateImageHighlightSelection(selectedAreaID);
    }
  } catch (e) {
    if (typeof window.ReactNativeWebView !== 'undefined') {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: '${errorEvent}',
          data: {
            error: e instanceof Error ? e.message : String(e),
            context: 'UPDATE_IMAGE_HIGHLIGHT_SELECTION_PARSE_ERROR'
          }
        })
      );
    } else if (window.parent !== window) {
      window.parent.postMessage({
        type: '${errorEvent}',
        data: {
          error: e instanceof Error ? e.message : String(e),
          context: 'UPDATE_IMAGE_HIGHLIGHT_SELECTION_PARSE_ERROR'
        }
      }, '*');
    }
  }
});

// Also listen on window for iOS compatibility
window.addEventListener('message', function(event) {
  try {
    const message = JSON.parse(event.data);
    if (message.type === '${updateSelectionEvent}') {
      const selectedAreaID = message.data.selectedAreaID;
      window.updateImageHighlightSelection(selectedAreaID);
    }
  } catch (e) {
    if (typeof window.ReactNativeWebView !== 'undefined') {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: '${errorEvent}',
          data: {
            error: e instanceof Error ? e.message : String(e),
            context: 'UPDATE_IMAGE_HIGHLIGHT_SELECTION_PARSE_ERROR'
          }
        })
      );
    } else if (window.parent !== window) {
      window.parent.postMessage({
        type: '${errorEvent}',
        data: {
          error: e instanceof Error ? e.message : String(e),
          context: 'UPDATE_IMAGE_HIGHLIGHT_SELECTION_PARSE_ERROR'
        }
      }, '*');
    }
  }
});

  `;
};

interface GenerateHTMLHighlightInjectionScriptInput {
  clickEvent: string;
  cssStyles: string;
  highlightClass: string;
  selectedClass: string;
  updateSelectionEvent: string;
  uuidDataAttribute: string;
}

export const generateHTMLHighlightInjectionScript = ({
  clickEvent,
  updateSelectionEvent,
  cssStyles,
  highlightClass,
  selectedClass,
  uuidDataAttribute,
}: GenerateHTMLHighlightInjectionScriptInput): string => {
  return `
// Inject CSS styles for highlights
if (!document.head) {
  const head = document.createElement('head');
  document.documentElement.insertBefore(head, document.body);
}

const style = document.createElement('style');
style.textContent = \`${cssStyles}\`;
document.head.appendChild(style);

// Setup highlight click handlers
function setupHighlightHandlers() {
  const highlightElements = document.querySelectorAll('.${highlightClass}');
  
  highlightElements.forEach(function(element) {
    element.addEventListener('click', function(e) {
      const searchInputUUID = e.currentTarget.getAttribute('${uuidDataAttribute}');
      
      if (!searchInputUUID) {
        return;
      }
      
      const message = {
        type: '${clickEvent}',
        data: {
          searchInputUUID: searchInputUUID
        }
      };
      
      if (typeof window.ReactNativeWebView !== 'undefined') {
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
      } else if (window.parent !== window) {
        window.parent.postMessage(message, '*');
      }
    });
  });
}

// Update selected highlight element
window.updateHTMLHighlightSelection = function(newSelectedUUID) {
  // Remove selection class from all elements
  const allHighlights = document.querySelectorAll('.${highlightClass}');
  allHighlights.forEach(function(element) {
    element.classList.remove('${selectedClass}');
  });
  
  // Add selection class to the new selected element
  if (newSelectedUUID) {
    const selectedElement = document.querySelector(
      '[${uuidDataAttribute}="' + newSelectedUUID + '"]'
    );
    
    if (selectedElement) {
      selectedElement.classList.add('${selectedClass}');
    }
  }
};

// Listen for selection update messages
window.addEventListener('message', function(event) {
  try {
    let message = event.data;
    
    // Handle string messages (from React Native)
    if (typeof message === 'string') {
      message = JSON.parse(message);
    }
    
    if (message.type === '${updateSelectionEvent}') {
      const selectedUUID = message.data.selectedSearchInputUUID;
      window.updateHTMLHighlightSelection(selectedUUID);
    }
  } catch (e) {
    // Only log parsing errors, don't send error events
    console.warn('Error parsing message:', e);
  }
});

// Also listen on document for iOS compatibility
document.addEventListener('message', function(event) {
  try {
    const message = JSON.parse(event.data);
    if (message.type === '${updateSelectionEvent}') {
      const selectedUUID = message.data.selectedSearchInputUUID;
      window.updateHTMLHighlightSelection(selectedUUID);
    }
  } catch (e) {
    console.warn('Error parsing message:', e);
  }
});

// Setup handlers when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupHighlightHandlers);
} else {
  setupHighlightHandlers();
}
  `;
};

/**
 * Delegated click handler injected into rendered order HTML so that external
 * links open in a new browser tab instead of navigating the embedded viewer.
 */
export const generateLinkInterceptScript = (): string => {
  return `
(function() {
  document.addEventListener('click', function(event) {
    if (event.defaultPrevented) {
      return;
    }

    const target = event.target;
    const anchor =
      target && typeof target.closest === 'function'
        ? target.closest('a[href]')
        : null;

    if (!anchor) {
      return;
    }

    const href = (anchor.getAttribute('href') || '').trim();
    if (!/^https?:/i.test(href)) {
      return;
    }

    event.preventDefault();
    // noreferrer matches the referrerPolicy the iframe is rendered with. It
    // also means window.open always returns null, so there is no way to tell
    // a blocked popup from a successful one - do not add a fallback on it.
    window.open(href, '_blank', 'noopener,noreferrer');
  });
})();
  `;
};
